// d:\typebattle\frontend\src\components\modals\LegalModal.jsx

import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Lock, FileText, X } from "lucide-react";
import { cn } from "@/utils/cn";
import { useModalA11y } from "@/utils/useModalA11y";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h3 className="text-sm font-bold text-white mb-2 uppercase tracking-wide">{title}</h3>
    <div className="text-xs text-zinc-400 leading-relaxed font-mono">{children}</div>
  </div>
);

const SUPPORT_EMAIL = "support@typebattle.win";

const SupportEmailLink = () => (
  <a
    href={`mailto:${SUPPORT_EMAIL}`}
    className="text-indigo-400 hover:text-indigo-300 underline underline-offset-4"
  >
    {SUPPORT_EMAIL}
  </a>
);

const LEGAL_CONTENT = {
  terms: {
    title: "Terms of Service",
    icon: FileText,
    date: "2026-08-08",
    content: (
      <>
        <Section title="1. Acceptance of Terms">
          By creating an account or otherwise using TypeBattle (the "Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.
        </Section>
        <Section title="2. Eligibility">
          You must be at least 13 years old to create an account. By registering, you confirm that you meet this requirement and that the information you provide is accurate.
        </Section>
        <Section title="3. Your Account">
          You're responsible for maintaining the confidentiality of your login credentials and for all activity under your account. Notify us immediately via <SupportEmailLink /> if you suspect unauthorized access.
        </Section>
        <Section title="4. Acceptable Use">
          You agree not to:
          <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
            <li>Use automated scripts, bots, macros, or other tools to gain an unfair typing advantage</li>
            <li>Attempt to manipulate match results, MMR, or leaderboard rankings through means other than normal play</li>
            <li>Probe, attack, or attempt to bypass the Service's security controls</li>
            <li>Impersonate another person or use abusive, offensive, or harassing usernames</li>
            <li>Interfere with other users' ability to use the Service</li>
          </ul>
          Violating any of the above may result in suspension or termination of your account without notice.
        </Section>
        <Section title="5. Public Statistics & Leaderboards">
          Your username, match history, and performance metrics (WPM, accuracy, MMR, win/loss record) are visible to other users on public leaderboards and match history screens. Don't use a username you wouldn't want publicly displayed.
        </Section>
        <Section title="6. Intellectual Property">
          The TypeBattle name, branding, and interface design are the property of its maintainers. Except where explicitly stated otherwise, no part of the Service may be copied, redistributed, or reused without permission.
        </Section>
        <Section title="7. Termination">
          We reserve the right to suspend or terminate your access to the Service at our discretion, with or without notice, for conduct that violates these terms or is otherwise harmful to the Service or other users.
        </Section>
        <Section title="8. Disclaimer of Warranties">
          The Service is provided "as is" and "as available," without warranties of any kind, express or implied. We don't guarantee it will be uninterrupted, error-free, or free of vulnerabilities.
        </Section>
        <Section title="9. Limitation of Liability">
          To the fullest extent permitted by law, TypeBattle and its maintainers are not liable for indirect, incidental, or consequential damages arising from your use of the Service, including loss of data, match history, or account statistics.
        </Section>
        <Section title="10. Changes to These Terms">
          We may update these terms from time to time; the "Last Updated" date above reflects the most recent revision. Continued use of the Service after a change takes effect constitutes acceptance of the revised terms.
        </Section>
        <Section title="11. Contact">
          Questions about these terms can be raised via <SupportEmailLink />.
        </Section>
      </>
    )
  },
  privacy: {
    title: "Privacy Policy",
    icon: Lock,
    date: "2026-08-08",
    content: (
      <>
        <Section title="1. Overview">
          This policy explains what data TypeBattle collects, why, and how it's handled. We collect only what's needed to run the Service — there's no advertising, no data resale, and no tracking beyond basic usage analytics.
        </Section>
        <Section title="2. Information We Collect">
          <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
            <li>Account information you provide (email address, username)</li>
            <li>Typing performance data generated by using the Service (WPM, accuracy, match results, MMR)</li>
            <li>Basic usage data (pages visited), collected via Firebase Analytics</li>
          </ul>
        </Section>
        <Section title="3. How We Use Information">
          <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
            <li>Operating core features — matchmaking, leaderboards, match history</li>
            <li>Maintaining account security and detecting abuse or cheating</li>
            <li>Understanding overall usage patterns to improve the Service</li>
          </ul>
        </Section>
        <Section title="4. Third-Party Services">
          TypeBattle is built on Google Firebase for authentication, database storage, and usage analytics. Firebase processes data on our behalf under Google's own privacy terms — we don't control how Google itself handles that data beyond what we send it. We don't use any other third-party analytics, advertising, or tracking services.
        </Section>
        <Section title="5. Cookies & Local Storage">
          We use browser local storage and session cookies to keep you signed in and remember your preferences. We don't use third-party advertising or cross-site tracking cookies.
        </Section>
        <Section title="6. Data Sharing">
          We don't sell or rent your personal data. Your username and performance statistics are visible to other users by design (see leaderboards and match history) — that's core to how the Service works, not a third-party disclosure. We may share anonymized, aggregated data (e.g. "average WPM across all users") that can't be traced back to you individually.
        </Section>
        <Section title="7. Data Retention & Deletion">
          Account and match data is retained for as long as your account remains active. To request deletion of your account and associated data, open a request via <SupportEmailLink />.
        </Section>
        <Section title="8. Children's Privacy">
          The Service is not directed at children under 13, and we don't knowingly collect data from them. If you believe a child has created an account, please report it via <SupportEmailLink /> so we can remove it.
        </Section>
        <Section title="9. Your Rights">
          You can review and update your account information at any time from your profile settings. For anything not self-serviceable — data export, deletion, or other requests — reach out via <SupportEmailLink />.
        </Section>
        <Section title="10. Changes to This Policy">
          We may update this policy as the Service evolves; the "Last Updated" date above reflects the most recent revision. Material changes will be reflected here.
        </Section>
      </>
    )
  },
  security: {
    title: "Security Protocols",
    icon: Shield,
    date: "2026-08-08",
    content: (
      <>
        <Section title="1. Encryption in Transit">
          All traffic between your browser and our servers is encrypted with industry-standard TLS. Unencrypted connections are not supported.
        </Section>
        <Section title="2. Authentication">
          Sign-in is handled by Firebase Authentication. We never see or store your password in plain text — Firebase handles credential hashing and storage using its own security-hardened infrastructure.
        </Section>
        <Section title="3. Data Access Controls">
          Access to stored data is enforced by database-level security rules, not just application code — every read and write is checked against your identity and permissions on the server side, independent of what the client claims. Sensitive fields (such as private-lobby passwords) are never exposed to users who aren't authorized to see them.
        </Section>
        <Section title="4. Anti-Cheat & Fair Play">
          Match outcomes, ranking changes, and completion times are validated server-side rather than trusted from the client, to limit the ability to falsify results or manipulate rankings.
        </Section>
        <Section title="5. Infrastructure">
          The Service runs on Google Firebase's hosting and database infrastructure, which provides encryption at rest, automated backups, and active abuse monitoring at the platform level.
        </Section>
        <Section title="6. Vulnerability Reporting">
          If you discover a security vulnerability, please report it responsibly through <SupportEmailLink /> rather than exploiting or publicly disclosing it first. We aim to acknowledge reports promptly and appreciate the help keeping the platform secure.
        </Section>
        <Section title="7. Your Responsibilities">
          Keep your account credentials private, use a unique password, and report anything that looks like unauthorized account activity as soon as you notice it.
        </Section>
      </>
    )
  }
};

export default function LegalModal({ isOpen, onClose, view }) {
  const data = LEGAL_CONTENT[view] || LEGAL_CONTENT.terms;
  const Icon = data.icon;
  const dialogRef = useRef(null);

  useModalA11y(isOpen, onClose, dialogRef);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm"
          />
          
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Legal information"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-zinc-900 border border-zinc-800 w-full max-w-2xl max-h-[80vh] flex flex-col rounded-2xl relative z-10 shadow-2xl shadow-black overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 md:p-8 border-b border-zinc-800 bg-zinc-900/50 flex items-start justify-between shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <Icon size={20} />
                </div>
                <div>
                  <h2 className="text-2xl font-black italic uppercase tracking-tighter text-white">
                    {data.title}
                  </h2>
                  <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mt-1">
                    Last Updated: {data.date}
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                aria-label="Close dialog"
                className="text-zinc-500 hover:text-white transition-colors p-1"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 md:p-8 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent">
              {data.content}
            </div>

            {/* Footer */}
            <div className="p-4 bg-zinc-950/50 border-t border-zinc-800 text-center shrink-0">
              <p className="text-[9px] text-zinc-600 font-mono uppercase tracking-widest">
                End of Document // {view?.toUpperCase()}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
